import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import brandTheme from "brandTheme/Theme";

const useStyles = makeStyles((theme) => ({
  section: {
    // padding: "80px 0",
    background: "#fff",
    paddingBottom:'56px',
  },

  container: {
    maxWidth: "1000px",
    margin: "0 auto",
    padding: "0 24px",

    [theme.breakpoints.down("sm")]: {
      padding: "0 16px",
    },
  },

  eyebrow: {
    textAlign: "center",
    color: "#ff5a00",
    fontSize: "14px",
    fontWeight: 800,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    marginBottom: "12px",
  },

  title: {
    textAlign: "center",
    fontSize: "32px",
    fontWeight: 800,
    color: "#171717",
    marginBottom: "60px",

    [theme.breakpoints.down("sm")]: {
      fontSize: "28px",
      marginBottom: "35px",
    },
  },

  cardsWrapper: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "24px",

    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },

  card: {
    borderRadius: "16px",
    overflow: "hidden",
    border: "1px solid #dedede",
    background: "#fff",
    boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
  },

  keyTermsHeader: {
    background: brandTheme.mainColor,
    color: "#fff",
    padding: "18px 24px",
    fontSize: "18px",
    fontWeight: 700,
  },

  exampleCard: {
    border: `1px solid ${brandTheme.secondaryColor}`,
    borderRadius: "16px",
    padding: "24px",
  },

  exampleBadge: {
    display: "inline-block",
    background: "#1cb17617",
    color: brandTheme.secondaryColor,
    padding: "6px 12px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: 700,
    marginBottom: "14px",
  },

  exampleDescription: {
    color: "#555",
    fontSize: "12px",
    marginBottom: "22px",
  },

  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: "52px",
    padding: "0 24px",
    borderBottom: "1px solid #e4e4e4",
    color: "#555",
    fontSize: "14px",
  },

  rowLast: {
    borderBottom: "none",
  },

  label: {
    color: "#555",
    fontWeight: 500,
  },

  value: {
    color: "#222",
    fontWeight: 700,
    textAlign: "right",
  },

  exampleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: "39px",
    borderBottom: "1px solid #e5e5e5",
    color: "#555",
    fontSize: "13px",
  },

  exampleRowLast: {
    borderBottom: "none",
  },

  emiValue: {
    color: brandTheme.secondaryColor,
    fontWeight: 800,
  },

  totalRepayment: {
    fontWeight: 800,
    color: "#222",
  },
}));

const keyTerms = [
  {
    label: "Loan amount",
    value: "₹1,000 – ₹2,00,000",
  },
  {
    label: "Tenure",
    value: "Up to 128 days",
  },
  {
    label: "Interest rate (ROI)",
    value: "36% p.a.",
  },
  {
    label: "APR",
    value: "Up to 75.99%",
  },
  {
    label: "Processing fee",
    value: "10% + GST",
  },
  {
    label: "Risk Assessment fee",
    value: "2% + GST",
  },
  {
    label: "Doc signing fee",
    value: "₹25",
  },
  {
    label: "Prepayment / Foreclosure",
    value: "Allowed (4%)",
  },
];

const representativeExample = [
  {
    label: "Loan amount",
    value: "₹50,000",
  },
  {
    label: "Tenure",
    value: "128 days (8 installments)",
  },
  {
    label: "Interest rate (ROI)",
    value: "36% p.a.",
  },
  {
    label: "Processing fee (10%)",
    value: "₹5,000 (10%)",
  },
  {
    label: "Risk assessment fee (2%)",
    value: "₹1,000 (2%)",
  },
  {
    label: "Doc signing fee",
    value: "₹25",
  },
  {
    label: "GST on fees (18%)",
    value: "₹1,084.50",
  },
  {
    label: "Total charges (incl. GST)",
    value: "₹7,110 (incl. GST)",
  },
  {
    label: "Total interest",
    value: "₹6,400",
  },
  {
    label: "EMI per installment",
    value: "₹7,050",
    highlight: true,
  },
  {
    label: "APR",
    value: "75.99%",
  },
  {
    label: "Amount disbursed",
    value: "₹42,890",
  },
  {
    label: "Total repayment",
    value: "₹56,400",
    total: true,
  },
];

export default function LoanAtAGlance() {
  const classes = useStyles();

  return (
    <section className={classes.section}>
      <div className={classes.container}>
        <div className={classes.cardsWrapper}>
          {/* Key Terms */}
          <Paper className={classes.card} elevation={0}>
            <div className={classes.keyTermsHeader}>
              Key Terms
            </div>

            {keyTerms.map((item, index) => (
              <div
                key={item.label}
                className={`${classes.row} ${
                  index === keyTerms.length - 1
                    ? classes.rowLast
                    : ""
                }`}
              >
                <span className={classes.label}>
                  {item.label}
                </span>

                <span className={classes.value}>
                  {item.value}
                </span>
              </div>
            ))}
          </Paper>

          {/* Representative Example */}
          <Paper className={classes.exampleCard} elevation={0}>
            <div className={classes.exampleBadge}>
              Representative Example (as required by RBI)
            </div>

            <Typography className={classes.exampleDescription}>
              This example is indicative. Actual rates depend on your
              credit profile.
            </Typography>

            {representativeExample.map((item, index) => (
              <div
                key={item.label}
                className={`${classes.exampleRow} ${
                  index === representativeExample.length - 1
                    ? classes.exampleRowLast
                    : ""
                }`}
              >
                <span className={classes.label}>
                  {item.label}
                </span>

                <span
                  className={
                    item.highlight
                      ? classes.emiValue
                      : item.total
                      ? classes.totalRepayment
                      : classes.value
                  }
                >
                  {item.value}
                </span>
              </div>
            ))}
          </Paper>
        </div>
      </div>
    </section>
  );
}